const TERMINAL_STATUSES = new Set(["achieved"]);
const INACTIVE_STATUSES = new Set(["achieved", "canceled", "archived"]);

function entityKeys(entity) {
    const value = entity?.member || entity;
    return [value?.documentId, value?.id]
        .filter((item) => item !== undefined && item !== null)
        .map(String);
}

export function isSameEntity(left, right) {
    const leftKeys = entityKeys(left);
    const rightKeys = new Set(entityKeys(right));
    return leftKeys.some((key) => rightKeys.has(key));
}

function containsEntity(items, target) {
    return (items || []).some((item) => isSameEntity(item, target));
}

function uniqueByIdentity(items) {
    const seen = new Set();
    return (items || []).filter((item) => {
        const key = String(item?.documentId || item?.id || "");
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function statusOf(item) {
    return item?.projectStatus || item?.goalStatus || item?.status;
}

function percent(part, total) {
    return total > 0 ? Math.round((part / total) * 100) : null;
}

function safeDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function differenceInDays(later, earlier) {
    return Math.max(0, (later.getTime() - earlier.getTime()) / 86400000);
}

function taskEventDate(task) {
    return safeDate(task?.doneAt) || safeDate(task?.updatedAt) || safeDate(task?.createdAt);
}

function taskCompletionDate(task) {
    if (task?.taskStatus !== "done") return null;
    return safeDate(task?.doneAt) || safeDate(task?.completedAt) || safeDate(task?.updatedAt);
}

function projectContainsMember(project, target, relatedTeams) {
    return isSameEntity(project?.owner, target)
        || containsEntity(project?.members, target)
        || relatedTeams.some((team) => isSameEntity(project?.team, team));
}

function goalContainsMember(goal, target, relatedTeams) {
    return isSameEntity(goal?.owner, target)
        || relatedTeams.some((team) => isSameEntity(goal?.team, team));
}

export function fallbackPresetForMember(member) {
    const source = String(member?.documentId || member?.id || member?.fullname || "member");
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) {
        hash = ((hash * 31) + source.charCodeAt(index)) >>> 0;
    }
    return `preset-avatar-${String((hash % 24) + 1).padStart(2, "0")}.png`;
}

export function buildProfileMetrics({
    member,
    membership,
    tasks = [],
    projects = [],
    goals = [],
    teams = [],
    workspaceMemberships = [],
    now = new Date(),
}) {
    const target = membership || member;
    const safeNow = safeDate(now) || new Date();
    const cleanTasks = uniqueByIdentity(tasks);
    const relatedTeams = uniqueByIdentity((teams || []).filter((team) => (
        isSameEntity(team?.owner, target) || containsEntity(team?.members, target)
    )));
    const taskProjectKeys = new Set(cleanTasks.flatMap((task) => (
        (task?.projects || []).flatMap(entityKeys)
    )));
    const relatedProjects = uniqueByIdentity((projects || []).filter((project) => (
        projectContainsMember(project, target, relatedTeams)
        || entityKeys(project).some((key) => taskProjectKeys.has(key))
    )));
    const managedProjects = relatedProjects.filter((project) => isSameEntity(project?.owner, target));
    const collaboratingProjects = relatedProjects.filter((project) => !isSameEntity(project?.owner, target));
    const relatedGoals = uniqueByIdentity((goals || []).filter((goal) => (
        goalContainsMember(goal, target, relatedTeams)
    )));
    const ownedGoals = relatedGoals.filter((goal) => isSameEntity(goal?.owner, target));
    const activeGoals = relatedGoals.filter((goal) => !INACTIVE_STATUSES.has(statusOf(goal)));

    const completedTasks = cleanTasks.filter((task) => task?.taskStatus === "done");
    const completedWithDeadline = completedTasks.filter((task) => safeDate(task?.dueDate) && taskCompletionDate(task));
    const onTimeTasks = completedWithDeadline.filter((task) => taskCompletionDate(task) <= safeDate(task.dueDate));
    const lateCompletedTasks = completedWithDeadline.filter((task) => taskCompletionDate(task) > safeDate(task.dueDate));
    const overdueOpenTasks = cleanTasks.filter((task) => (
        task?.taskStatus !== "done" && safeDate(task?.dueDate) && safeDate(task.dueDate) < safeNow
    ));
    const delayedTasks = [...lateCompletedTasks, ...overdueOpenTasks];
    const delayDays = delayedTasks.map((task) => {
        const dueDate = safeDate(task.dueDate);
        const endDate = task.taskStatus === "done" ? taskCompletionDate(task) : safeNow;
        return endDate && dueDate ? differenceInDays(endDate, dueDate) : 0;
    });

    const achievedManagedProjects = managedProjects.filter((project) => TERMINAL_STATUSES.has(statusOf(project)));
    const achievedCollaboratingProjects = collaboratingProjects.filter((project) => TERMINAL_STATUSES.has(statusOf(project)));
    const achievedOwnedGoals = ownedGoals.filter((goal) => TERMINAL_STATUSES.has(statusOf(goal)));
    const responsibilityTotal = cleanTasks.length + managedProjects.length + ownedGoals.length;
    const responsibilityDone = completedTasks.length + achievedManagedProjects.length + achievedOwnedGoals.length;

    const weekdayOrder = [6, 0, 1, 2, 3, 4, 5];
    const weekdayActivity = weekdayOrder.map((day) => cleanTasks.filter((task) => taskEventDate(task)?.getDay() === day).length);

    const monthFormatter = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {month: "long"});
    const monthBuckets = [];
    for (let offset = 5; offset >= 0; offset -= 1) {
        const start = new Date(safeNow.getFullYear(), safeNow.getMonth() - offset, 1);
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
        monthBuckets.push({
            label: monthFormatter.format(start),
            assigned: cleanTasks.filter((task) => {
                const date = safeDate(task?.createdAt);
                return date && date >= start && date < end;
            }).length,
            completed: cleanTasks.filter((task) => {
                const date = safeDate(task?.doneAt);
                return date && date >= start && date < end;
            }).length,
        });
    }

    const coworkerRelations = new Map();
    const registerCoworker = (candidate, type, relationId) => {
        if (!candidate || isSameEntity(candidate, target)) return;
        const candidateMember = candidate?.member || candidate;
        const candidateMembership = (workspaceMemberships || []).find((item) => isSameEntity(item, candidate)) || candidate;
        const key = String(candidateMembership?.documentId || candidateMembership?.id || candidateMember?.documentId || candidateMember?.id || "");
        if (!key) return;
        const current = coworkerRelations.get(key) || {membership: candidateMembership, projectIds: new Set(), teamIds: new Set()};
        current[`${type}Ids`].add(String(relationId));
        coworkerRelations.set(key, current);
    };
    relatedProjects.forEach((project) => {
        const relationId = project?.documentId || project?.id;
        registerCoworker(project?.owner, "project", relationId);
        (project?.members || []).forEach((candidate) => registerCoworker(candidate, "project", relationId));
    });
    relatedTeams.forEach((team) => {
        const relationId = team?.documentId || team?.id;
        registerCoworker(team?.owner, "team", relationId);
        (team?.members || []).forEach((candidate) => registerCoworker(candidate, "team", relationId));
    });
    const coworkers = Array.from(coworkerRelations.values()).map((item) => ({
        membership: item.membership,
        sharedProjects: item.projectIds.size,
        sharedTeams: item.teamIds.size,
    }));

    const latestActivities = [...cleanTasks]
        .filter(taskEventDate)
        .sort((left, right) => taskEventDate(right) - taskEventDate(left))
        .slice(0, 8)
        .map((task) => ({...task, activityAt: taskEventDate(task)}));

    return {
        member,
        membership,
        tasks: cleanTasks,
        completedTasks,
        relatedProjects,
        managedProjects,
        collaboratingProjects,
        relatedGoals,
        activeGoals,
        ownedGoals,
        relatedTeams,
        coworkers,
        latestActivities,
        weekdayActivity,
        monthBuckets,
        fallbackPreset: fallbackPresetForMember(member),
        stats: {
            projects: relatedProjects.length,
            activeGoals: activeGoals.length,
            completedTasks: completedTasks.length,
            tasks: cleanTasks.length,
            teams: relatedTeams.length,
            collaborations: collaboratingProjects.length,
            delays: delayedTasks.length,
            completionRate: percent(completedTasks.length, cleanTasks.length),
            onTimeRate: percent(onTimeTasks.length, completedWithDeadline.length),
            averageDelayDays: delayDays.length
                ? delayDays.reduce((sum, value) => sum + value, 0) / delayDays.length
                : 0,
            overallSuccessRate: percent(responsibilityDone, responsibilityTotal),
            managedProjectSuccessRate: percent(achievedManagedProjects.length, managedProjects.length),
            goalSuccessRate: percent(achievedOwnedGoals.length, ownedGoals.length),
            collaborationSuccessRate: percent(achievedCollaboratingProjects.length, collaboratingProjects.length),
        },
    };
}
